import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  IconArrowLeft,
  IconChevronDown,
  IconDeviceFloppy,
  IconFileImport,
  IconLanguage,
  IconPlus,
  IconStar,
} from "@tabler/icons-react";
import { getIn, useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import "@assets/css/before_you_go/panel.css";
import "@assets/css/mode_tool/panel.css";
import "@assets/css/mode_tool/translatePointsModal.css";
import AiToolsPanel from "@components/ai_tools/AiToolsPanel";
import InlineError from "@components/forms/InlineError";
import AppModal from "@components/modals/AppModal";
import ThinkingAnimation from "@utils/ui/ThinkingAnimation";
import MediaPicker from "@components/media/MediaPicker";
import useStartPointLanguages from "@hooks/start_point/useStartPointLanguages";
import useTourSectionMutations from "@hooks/tour_sections/useTourSectionMutations";
import { uploadTourSectionImage } from "@utils/tour_sections/sectionImageUpload";
import {
  createQuillEditor,
  destroyQuillEditor,
  getQuillHtml,
} from "@utils/quill/editor";
import { TOUR_SECTION_ICON_OPTIONS } from "./shared/constants";
import {
  importBeforeYouGoJsonFile,
  importMaximizeExperienceJsonFile,
} from "@utils/tour_sections/importBeforeYouGoJson";
import {
  createEmptyItem,
  createEmptySection,
  normalizeSeedSections,
  pickTourName,
  readLocalizedValue,
  reorderItemsWithSequentialOrder,
  reorderSectionsWithSequentialOrder,
} from "./shared/helpers";
import SortableSectionCard from "./shared/SortableSectionCard";
import SortableSectionItemCard from "./shared/SortableSectionItemCard";
import { buildTourSectionValidationSchema } from "./shared/validation";
import { translateGenericStream } from "@services/ai/aiApi";
import {
  BEFORE_YOU_GO_ITEM_DESCRIPTION_MAX_CHARS,
  getCountableEditorTextFromHtml,
  getCountableTextFromQuill,
  limitQuillPlainText,
  preventQuillInputOverLimit,
} from "./shared/quillTextLimit";

function BaseTourSectionPanel({
  activeTour,
  languages,
  initialSections = [],
  loadingSections = false,
  panelTitle = "Before you go",
  onClose,
  onSave,
}) {
  const languageList = useStartPointLanguages(languages);
  const [activeLanguageIso, setActiveLanguageIso] = useState("");
  const [lockSaveUntilValid, setLockSaveUntilValid] = useState(false);
  const activeIsos = useMemo(
    () => (languageList || []).map((language) => language.iso).filter(Boolean),
    [languageList],
  );
  const activeTourKey = String(
    activeTour?.id || activeTour?.tur_id || activeTour?.tourId || activeTour?.code || "",
  );
  const isBeforeYouGoPanel = panelTitle
    .toLowerCase()
    .includes("before you go");
  const isMaximizeExperiencePanel = panelTitle
    .toLowerCase()
    .includes("maximize");
  const supportsJsonImport =
    isBeforeYouGoPanel || isMaximizeExperiencePanel;
  const validationSchema = useMemo(
    () => buildTourSectionValidationSchema(activeIsos),
    [activeIsos],
  );
  const [sections, setSections] = useState([]);
  const [expandedSectionId, setExpandedSectionId] = useState("");
  const [iconPickerOpenSectionId, setIconPickerOpenSectionId] = useState("");
  const [uploadingImageBySectionId, setUploadingImageBySectionId] = useState(
    {},
  );
  const [imageErrorBySectionId, setImageErrorBySectionId] = useState({});
  const [uploadingImageByItemId, setUploadingImageByItemId] = useState({});
  const [imageErrorByItemId, setImageErrorByItemId] = useState({});
  const [editor, setEditor] = useState({
    isOpen: false,
    target: "section",
    sectionId: "",
    itemId: "",
    languageIso: "",
  });
  const [editorHtml, setEditorHtml] = useState("");
  const [quillHost, setQuillHost] = useState(null);
  const [isDescriptionEditorReady, setIsDescriptionEditorReady] =
    useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isImportingJson, setIsImportingJson] = useState(false);
  const [isTranslateModalOpen, setIsTranslateModalOpen] = useState(false);
  const [translateSectionId, setTranslateSectionId] = useState(null);
  const [translateLanguage, setTranslateLanguage] = useState("");
  const [isTranslating, setIsTranslating] = useState(false);
  const [translateError, setTranslateError] = useState("");
  const [translateComplete, setTranslateComplete] = useState(false);
  const [translateProgress, setTranslateProgress] = useState({
    points: [],
    total: 0,
    completed: 0,
    done: false,
  });
  const [importJsonError, setImportJsonError] = useState("");
  const [importJsonProgress, setImportJsonProgress] = useState("");
  const importJsonInputRef = useRef(null);
  const aiToolsAnchorRef = useRef(null);
  const [quillState, setQuillState] = useState({
    instance: null,
    handler: null,
  });
  const formik = useFormik({
    initialValues: {
      sections: [],
    },
    validateOnMount: true,
    validationSchema,
    onSubmit: () => {},
  });
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );
  const {
    updateSections,
    updateSection,
    updateSectionField,
    updateSectionLocalizedField,
    updateItem,
    updateItemField,
    updateItemLocalizedField,
  } = useTourSectionMutations(setSections, formik);

  useEffect(() => {
    if (!languageList.length) {
      setActiveLanguageIso("");
      return;
    }
    const englishLanguage = languageList.find(
      (language) => language.iso === "en",
    );
    setActiveLanguageIso(englishLanguage?.iso || languageList[0].iso);
  }, [activeTourKey, languageList]);

  useEffect(() => {
    if (!activeIsos.length) return;
    const normalized = normalizeSeedSections(initialSections, activeIsos);
    setSections(normalized);
    setExpandedSectionId("");
    formik.setFieldValue("sections", normalized, true);
  }, [activeIsos, initialSections]);

  useEffect(() => {
    if (lockSaveUntilValid && formik.isValid) {
      setLockSaveUntilValid(false);
    }
  }, [formik.isValid, lockSaveUntilValid]);

  const hasResolvedActiveLanguage = activeIsos.includes(activeLanguageIso);
  const visibleLanguageIso = hasResolvedActiveLanguage ? activeLanguageIso : "";
  const isEnglishLanguage = String(visibleLanguageIso).toLowerCase() === "en";
  const translationLanguages = useMemo(
    () => languageList.filter((language) => language.iso !== "en"),
    [languageList],
  );
  const shouldLimitActiveEditor =
    isBeforeYouGoPanel && editor.isOpen && editor.target === "item";
  const editorPlainTextLength = useMemo(() => {
    if (!shouldLimitActiveEditor) return 0;
    if (quillState.instance) {
      return getCountableTextFromQuill(quillState.instance).length;
    }
    return getCountableEditorTextFromHtml(editorHtml).length;
  }, [editorHtml, quillState.instance, shouldLimitActiveEditor]);
  const requiredLanguageIso = useMemo(() => {
    if (activeIsos.includes("en")) return "en";
    return activeIsos[0] || "";
  }, [activeIsos]);
  const translationTotal = Number(translateProgress.total) || 0;
  const translationCompleted = Number(translateProgress.completed) || 0;
  const translationPercent = translationTotal
    ? Math.min(100, Math.round((translationCompleted / translationTotal) * 100))
    : 0;
  const translationCounts = translateProgress.points.reduce((counts, point) => {
    const status = String(point?.status || "pending");
    counts[status] = (counts[status] || 0) + 1;
    return counts;
  }, {});
  const translateTargetSection = translateSectionId === null
    ? null
    : sections.find((section) => String(section.id) === translateSectionId) || null;
  const translateTargetSectionTitle = translateTargetSection
    ? readLocalizedValue(translateTargetSection?.title?.[visibleLanguageIso]) ||
      readLocalizedValue(translateTargetSection?.title?.en)
    : "";
  const hasTranslateTarget = translateSectionId === null
    ? sections.length > 0
    : Boolean(translateTargetSection);

  useEffect(() => {
    if (!editor.isOpen) return;
    if (!quillHost || quillState.instance) return;
    const quill = createQuillEditor({
      container: quillHost,
      initialHtml: editorHtml,
    });
    if (!quill) return;
    const shouldLimitItemDescription =
      isBeforeYouGoPanel && editor.target === "item";
    const keydownHandler = (event) => {
      if (!shouldLimitItemDescription) return;
      preventQuillInputOverLimit(
        event,
        quill,
        BEFORE_YOU_GO_ITEM_DESCRIPTION_MAX_CHARS,
      );
    };
    const handler = () => {
      if (shouldLimitItemDescription) {
        limitQuillPlainText(quill, BEFORE_YOU_GO_ITEM_DESCRIPTION_MAX_CHARS);
      }
      setEditorHtml(getQuillHtml(quill));
    };
    if (shouldLimitItemDescription) {
      limitQuillPlainText(quill, BEFORE_YOU_GO_ITEM_DESCRIPTION_MAX_CHARS);
    }
    quill.root.addEventListener("keydown", keydownHandler);
    quill.on("text-change", handler);
    setQuillState({ instance: quill, handler, keydownHandler });
    setIsDescriptionEditorReady(true);
  }, [
    editor.isOpen,
    editor.target,
    editorHtml,
    isBeforeYouGoPanel,
    quillHost,
    quillState.instance,
  ]);

  useEffect(() => {
    if (editor.isOpen || !quillState.instance) return;
    destroyQuillEditor({
      container: quillHost,
      quill: quillState.instance,
      textChangeHandler: quillState.handler,
    });
    if (quillState.keydownHandler) {
      quillState.instance.root.removeEventListener(
        "keydown",
        quillState.keydownHandler,
      );
    }
    setQuillState({ instance: null, handler: null, keydownHandler: null });
    setIsDescriptionEditorReady(false);
    setQuillHost(null);
  }, [editor.isOpen, quillHost, quillState]);

  if (!activeTour) return null;

  function handleAddSection() {
    const nextSection = createEmptySection(sections, activeIsos);
    updateSections((currentSections) => [...currentSections, nextSection]);
    setExpandedSectionId(nextSection.id);
  }

  async function handleImportTourSectionJson(file) {
    if (!supportsJsonImport || !file || isImportingJson) return;
    const importLabel = isBeforeYouGoPanel
      ? "Before You Go"
      : "Maximize Your Experience";
    if (
      sections.length &&
      !window.confirm(
        `Are you sure you want to replace the current ${importLabel} sections with this JSON file?`,
      )
    ) {
      return;
    }

    setIsImportingJson(true);
    setImportJsonError("");
    setImportJsonProgress("Reading JSON file...");
    try {
      const importer = isBeforeYouGoPanel
        ? importBeforeYouGoJsonFile
        : importMaximizeExperienceJsonFile;
      const importedSections = await importer(
        file,
        activeIsos,
        setImportJsonProgress,
      );
      updateSections(importedSections, true);
      setExpandedSectionId(importedSections[0]?.id || "");
      if (activeIsos.includes("en")) setActiveLanguageIso("en");
    } catch (error) {
      setImportJsonError(
        error?.message || `Could not import ${importLabel} JSON.`,
      );
      setImportJsonProgress("");
    } finally {
      setIsImportingJson(false);
    }
  }

  function handleRemoveSection(sectionId) {
    updateSections((currentSections) =>
      currentSections
        .filter((section) => section.id !== sectionId)
        .map((section, index) => ({
          ...section,
          order: index + 1,
        })),
    );
    setExpandedSectionId((currentId) =>
      currentId === sectionId ? "" : currentId,
    );
  }

  function handleDragEnd(event) {
    const { active, over } = event || {};
    const activeId = String(active?.id || "");
    const overId = String(over?.id || "");
    if (!activeId || !overId || activeId === overId) return;
    updateSections((currentSections) => {
      const oldIndex = currentSections.findIndex(
        (section) => String(section.id) === activeId,
      );
      const newIndex = currentSections.findIndex(
        (section) => String(section.id) === overId,
      );
      if (oldIndex < 0 || newIndex < 0) return currentSections;
      return reorderSectionsWithSequentialOrder(
        currentSections,
        oldIndex,
        newIndex,
      );
    });
  }

  function handleItemDragEnd(sectionId, event) {
    const { active, over } = event || {};
    const activeId = String(active?.id || "");
    const overId = String(over?.id || "");
    if (!activeId || !overId || activeId === overId) return;
    updateSection(sectionId, (section) => {
      const items = Array.isArray(section?.items) ? section.items : [];
      const oldIndex = items.findIndex((item) => String(item.id) === activeId);
      const newIndex = items.findIndex((item) => String(item.id) === overId);
      if (oldIndex < 0 || newIndex < 0) return section;
      return {
        ...section,
        items: reorderItemsWithSequentialOrder(items, oldIndex, newIndex),
      };
    });
  }

  function handleAddItem(sectionId, itemType = "hotel") {
    updateSection(sectionId, (section) => ({
      ...section,
      items: [
        ...(section.items || []),
        createEmptyItem(sectionId, section.items, activeIsos, itemType),
      ],
    }));
  }

  function handleRemoveItem(sectionId, itemId) {
    updateSection(sectionId, (section) => ({
      ...section,
      items: (section.items || [])
        .filter((item) => item.id !== itemId)
        .map((item, index) => ({
          ...item,
          order: index + 1,
          displayOrder: index + 1,
        })),
    }));
  }

  async function handleUploadSectionImage(sectionId, files) {
    if (!Array.isArray(files) || !files.length) return;
    setUploadingImageBySectionId((prev) => ({ ...prev, [sectionId]: true }));
    setImageErrorBySectionId((prev) => ({ ...prev, [sectionId]: "" }));
    try {
      const imageData = await uploadTourSectionImage(files[0], sectionId);
      updateSection(sectionId, (section) => ({
        ...section,
        ...imageData,
      }));
    } catch (error) {
      setImageErrorBySectionId((prev) => ({
        ...prev,
        [sectionId]: error?.message || "Could not upload image.",
      }));
    } finally {
      setUploadingImageBySectionId((prev) => ({ ...prev, [sectionId]: false }));
    }
  }

  async function handleUploadItemImage(sectionId, itemId, files) {
    if (!Array.isArray(files) || !files.length) return;
    setUploadingImageByItemId((prev) => ({ ...prev, [itemId]: true }));
    setImageErrorByItemId((prev) => ({ ...prev, [itemId]: "" }));
    try {
      const imageData = await uploadTourSectionImage(files[0], itemId);
      updateItem(sectionId, itemId, (item) => ({
        ...item,
        ...imageData,
      }));
    } catch (error) {
      setImageErrorByItemId((prev) => ({
        ...prev,
        [itemId]: error?.message || "Could not upload image.",
      }));
    } finally {
      setUploadingImageByItemId((prev) => ({ ...prev, [itemId]: false }));
    }
  }

  function openDescriptionEditor(sectionId, languageIso) {
    const section = sections.find((item) => item.id === sectionId);
    setEditor({
      isOpen: true,
      target: "section",
      sectionId,
      itemId: "",
      languageIso,
    });
    setEditorHtml(readLocalizedValue(section?.descriptions?.[languageIso]));
  }

  function openItemDescriptionEditor(sectionId, itemId, languageIso) {
    const section = sections.find((item) => item.id === sectionId);
    const item = (section?.items || []).find((entry) => entry.id === itemId);
    setEditor({
      isOpen: true,
      target: "item",
      sectionId,
      itemId,
      languageIso,
    });
    setEditorHtml(readLocalizedValue(item?.description?.[languageIso]));
  }

  function closeDescriptionEditor() {
    setEditor({
      isOpen: false,
      target: "section",
      sectionId: "",
      itemId: "",
      languageIso: "",
    });
    setEditorHtml("");
    setIsDescriptionEditorReady(false);
  }

  function saveDescriptionEditor() {
    if (!editor.sectionId) return;
    if (editor.target === "item") {
      if (!editor.itemId || !editor.languageIso) return;
      if (isBeforeYouGoPanel && quillState.instance) {
        limitQuillPlainText(
          quillState.instance,
          BEFORE_YOU_GO_ITEM_DESCRIPTION_MAX_CHARS,
        );
      }
      updateItemLocalizedField(
        editor.sectionId,
        editor.itemId,
        "description",
        editor.languageIso,
        isBeforeYouGoPanel && quillState.instance
          ? getQuillHtml(quillState.instance)
          : editorHtml || "",
      );
      closeDescriptionEditor();
      return;
    }
    if (!editor.languageIso) return;
    updateSectionLocalizedField(
      editor.sectionId,
      "descriptions",
      editor.languageIso,
      editorHtml,
    );
    formik.setFieldTouched(
      `sections.${sections.findIndex((item) => item.id === editor.sectionId)}.descriptions.${editor.languageIso}`,
      true,
      true,
    );
    closeDescriptionEditor();
  }

  function openTranslateModal(sectionId = null) {
    setTranslateSectionId(sectionId === null ? null : String(sectionId));
    setTranslateLanguage("");
    setTranslateError("");
    setTranslateComplete(false);
    setTranslateProgress({ points: [], total: 0, completed: 0, done: false });
    setIsTranslateModalOpen(true);
  }

  async function handleTranslateSections() {
    const sectionsToTranslate = translateSectionId === null
      ? sections
      : sections.filter((section) => String(section.id) === translateSectionId);
    if (!translateLanguage || !sectionsToTranslate.length) return;
    const source = sectionsToTranslate.map((section) => ({
      id: String(section.id),
      title: readLocalizedValue(section?.title?.en),
      description: readLocalizedValue(section?.descriptions?.en),
      items: (Array.isArray(section?.items) ? section.items : []).map(
        (item) => ({
          id: String(item.id),
          title: readLocalizedValue(item?.title?.en),
          description: readLocalizedValue(item?.description?.en),
        }),
      ),
    }));
    setIsTranslating(true);
    setTranslateError("");
    setTranslateComplete(false);
    setTranslateProgress({
      points: source.map((section, index) => ({
        id: section.id,
        label: section.title || `Section ${index + 1}`,
        status: "pending",
      })),
      total: source.length,
      completed: 0,
      done: false,
    });
    try {
      const pointStatuses = new Map();
      const pointErrors = new Map();
      const response = await translateGenericStream(
        { language: translateLanguage, data: source },
        ({ event, data }) => {
          if (event === "start") {
            setTranslateProgress((current) => ({
              ...current,
              total: Number(data?.total) || current.total,
            }));
            return;
          }
          if (event !== "point" || !data?.id) return;
          const id = String(data.id);
          pointStatuses.set(id, String(data.status || ""));
          if (data.status === "error") {
            pointErrors.set(id, data?.message || "Translation failed.");
          }
          setTranslateProgress((current) => {
            const points = current.points.map((point) =>
              point.id === id
                ? {
                    ...point,
                    status: data.status === "error" ? "error" : "completed",
                    message:
                      data?.status === "error"
                        ? data?.message || "Translation failed."
                        : "",
                    translatedName:
                      data?.data?.title || data?.data?.data?.title || "",
                  }
                : point,
            );
            return {
              ...current,
              points,
              completed: points.filter(
                (point) => point.status === "completed" || point.status === "error",
              ).length,
            };
          });
        },
      );
      const translated = Array.isArray(response?.body?.data)
        ? response.body.data
        : [];
      if (response?.error || !translated.length) {
        throw new Error(
          response?.message || "The translation response did not include the sections.",
        );
      }

      const translatedSections = new Map(
        translated.map((section) => [String(section?.id || ""), section]),
      );
      const languageIso = translateLanguage;
      updateSections(
        (currentSections) =>
          currentSections.map((section) => {
            if (pointStatuses.get(String(section.id)) === "error") return section;
            const translatedSection = translatedSections.get(String(section.id));
            if (!translatedSection) return section;
            const translatedItems = new Map(
              (Array.isArray(translatedSection.items)
                ? translatedSection.items
                : []
              ).map((item) => [String(item?.id || ""), item]),
            );
            return {
              ...section,
              title: {
                ...(section.title || {}),
                [languageIso]: String(translatedSection.title ?? section.title?.en ?? ""),
              },
              descriptions: {
                ...(section.descriptions || {}),
                [languageIso]: String(translatedSection.description ?? section.descriptions?.en ?? ""),
              },
              items: (section.items || []).map((item) => {
                const translatedItem = translatedItems.get(String(item.id));
                if (!translatedItem) return item;
                return {
                  ...item,
                  title: {
                    ...(item.title || {}),
                    [languageIso]: String(translatedItem.title ?? item.title?.en ?? ""),
                  },
                  description: {
                    ...(item.description || {}),
                    [languageIso]: String(translatedItem.description ?? item.description?.en ?? ""),
                  },
                };
              }),
            };
          }),
        false,
      );
      setTranslateComplete(true);
      setTranslateProgress((current) => ({ ...current, done: true }));
      if (pointErrors.size) {
        setTranslateError(
          `${pointErrors.size} section${pointErrors.size === 1 ? "" : "s"} could not be translated. Other completed sections were added to the draft.`,
        );
      }
    } catch (error) {
      setTranslateProgress((current) => ({ ...current, done: true }));
      setTranslateError(error?.message || `Could not translate ${panelTitle}.`);
    } finally {
      setIsTranslating(false);
    }
  }

  async function handleSaveBeforeYouGo() {
    const errors = await formik.validateForm();
    if (Object.keys(errors || {}).length) {
      touchAllRequiredFields();
      setLockSaveUntilValid(true);
      return;
    }
    if (typeof onSave !== "function") return;
    setIsSaving(true);
    try {
      await onSave({
        tour: activeTour,
        sections,
      });
    } catch (error) {
    } finally {
      setIsSaving(false);
    }
  }

  function getFieldError(path) {
    if (visibleLanguageIso !== requiredLanguageIso) return "";
    const errorValue = getIn(formik.errors, path);
    return errorValue ? String(errorValue) : "";
  }

  function touchAllRequiredFields() {
    formik.setTouched({
      sections: sections.map((section) => ({
        title: requiredLanguageIso ? { [requiredLanguageIso]: true } : {},
        iconName: true,
        imageUrl: true,
        descriptions: requiredLanguageIso
          ? { [requiredLanguageIso]: true }
          : {},
        items: (section.items || []).map(() => ({
          itemType: true,
          title: requiredLanguageIso ? { [requiredLanguageIso]: true } : {},
          location: true,
          rating: true,
          websiteUrl: true,
          imageUrl: true,
        })),
      })),
    });
  }

  return (
    <aside className="before-you-go-tool-overlay" ref={aiToolsAnchorRef}>
      <div className="mode-route-tool-header before-you-go-tool-header">
        {languageList.length ? (
          <div className="app-language-section">
            <div className="app-language-switcher" aria-label="Languages">
              {languageList.map((language) => (
                <button
                  key={language.id || language.iso}
                  type="button"
                  className={`app-language-btn ${visibleLanguageIso === language.iso ? "is-active" : ""}`}
                  onClick={() => setActiveLanguageIso(language.iso)}
                  aria-pressed={visibleLanguageIso === language.iso}
                  title={language.name || language.iso.toUpperCase()}
                >
                  {language.iso.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <div className="mode-route-tool-head">
          <button
            type="button"
            className="app-icon-btn app-icon-btn--back"
            aria-label="Go back"
            title="Go back"
            onClick={onClose}
          >
            <IconArrowLeft size={16} />
          </button>
          {supportsJsonImport ? (
            <>
              <input
                ref={importJsonInputRef}
                type="file"
                accept=".json,application/json"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  handleImportTourSectionJson(file);
                  event.target.value = "";
                }}
              />
              <button
                type="button"
                className={`app-icon-btn app-icon-btn--update ${isImportingJson ? "is-loading" : ""}`}
                aria-label={`Import ${panelTitle} JSON`}
                title={`Import ${panelTitle} JSON`}
                disabled={isImportingJson}
                onClick={() => importJsonInputRef.current?.click()}
              >
                {isImportingJson ? (
                  <span className="mode-route-tool-btn-spinner" />
                ) : (
                  <IconFileImport size={17} />
                )}
              </button>
            </>
          ) : null}
          <button
            type="button"
            className="app-icon-btn app-icon-btn--update"
            aria-label="Add section"
            title="Add section"
            onClick={handleAddSection}
          >
            <IconPlus size={18} />
          </button>
          <h3 className="mode-route-tool-title before-you-go-tool-title">
            {panelTitle} - {pickTourName(activeTour)}
          </h3>
        </div>
      </div>

      <section className="before-you-go-tool-panel">
        {importJsonError ? (
          <p className="message-error before-you-go-tool-import-message">
            {importJsonError}
          </p>
        ) : null}
        {isImportingJson && importJsonProgress ? (
          <p className="before-you-go-tool-help before-you-go-tool-import-message">
            {importJsonProgress}
          </p>
        ) : null}
        {loadingSections ? (
          <p className="before-you-go-tool-help">Loading before you go...</p>
        ) : null}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sections.map((section) => section.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="before-you-go-tool-list">
              {sections.map((section) => {
                const isOpen = expandedSectionId === section.id;
                const CurrentSectionIcon =
                  TOUR_SECTION_ICON_OPTIONS.find(
                    (option) => option.value === (section?.iconName || "star"),
                  )?.Icon || IconStar;

                return (
                  <SortableSectionCard
                    key={section.id}
                    section={section}
                    visibleLanguageIso={visibleLanguageIso}
                    isOpen={isOpen}
                    onTranslate={
                      isEnglishLanguage
                        ? () => openTranslateModal(section.id)
                        : undefined
                    }
                    onToggle={() =>
                      setExpandedSectionId((currentId) =>
                        currentId === section.id ? "" : section.id,
                      )
                    }
                    onDelete={() => handleRemoveSection(section.id)}
                  >
                    {isOpen ? (
                      <div className="before-you-go-tool-form">
                        <div>
                          <p className="tour-add-form-label">
                            title
                            {getFieldError(
                              `sections.${sections.findIndex((entry) => entry.id === section.id)}.title.${visibleLanguageIso}`,
                            ) ? (
                              <InlineError>
                                {getFieldError(
                                  `sections.${sections.findIndex((entry) => entry.id === section.id)}.title.${visibleLanguageIso}`,
                                )}
                              </InlineError>
                            ) : null}
                          </p>
                          <label className="field-label app-form-field">
                            <input
                              className="field-input"
                              value={section?.title?.[visibleLanguageIso] || ""}
                              onChange={(event) =>
                                updateSectionLocalizedField(
                                  section.id,
                                  "title",
                                  visibleLanguageIso,
                                  event.target.value,
                                )
                              }
                              onBlur={() =>
                                formik.setFieldTouched(
                                  `sections.${sections.findIndex((entry) => entry.id === section.id)}.title.${visibleLanguageIso}`,
                                  true,
                                  true,
                                )
                              }
                            />
                          </label>
                        </div>

                        {isEnglishLanguage ? <div className="before-you-go-tool-icon-field">
                          <span className="label-inline before-you-go-tool-icon-label">
                            Icon
                          </span>
                          <div className="before-you-go-tool-icon-picker">
                            <button
                              type="button"
                              className="before-you-go-tool-icon-trigger"
                              onClick={() =>
                                setIconPickerOpenSectionId((currentId) =>
                                  currentId === section.id ? "" : section.id,
                                )
                              }
                            >
                              <span className="before-you-go-tool-icon-trigger-main">
                                <CurrentSectionIcon size={15} />
                                <span>
                                  {TOUR_SECTION_ICON_OPTIONS.find(
                                    (option) =>
                                      option.value ===
                                      (section.iconName || "star"),
                                  )?.label || "Star"}
                                </span>
                              </span>
                              <IconChevronDown size={14} />
                            </button>
                            {iconPickerOpenSectionId === section.id ? (
                              <div className="before-you-go-tool-icon-menu">
                                {TOUR_SECTION_ICON_OPTIONS.map((option) => {
                                  const OptionIcon = option.Icon;
                                  const isActive =
                                    option.value ===
                                    (section.iconName || "star");
                                  return (
                                    <button
                                      key={option.value}
                                      type="button"
                                      className={`before-you-go-tool-icon-item ${isActive ? "active" : ""}`}
                                      onClick={() => {
                                        updateSectionField(
                                          section.id,
                                          "iconName",
                                          option.value,
                                        );
                                        setIconPickerOpenSectionId("");
                                      }}
                                    >
                                      <OptionIcon size={14} />
                                      <span>{option.label}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        </div> : null}

                        {isEnglishLanguage ? <MediaPicker
                          title="image (required)"
                          titleError={
                            getFieldError(
                              `sections.${sections.findIndex((entry) => entry.id === section.id)}.imageUrl`,
                            ) || ""
                          }
                          items={section?.image ? [section.image] : []}
                          isUploading={Boolean(
                            uploadingImageBySectionId[section.id],
                          )}
                          uploadError={imageErrorBySectionId[section.id] || ""}
                          accept={{ "image/*": [] }}
                          maxFiles={1}
                          invalidTypeMessage="Image only for this section."
                          onUpload={(files) =>
                            handleUploadSectionImage(section.id, files)
                          }
                          onRemove={() =>
                            updateSection(section.id, (currentSection) => ({
                              ...currentSection,
                              imageUrl: "",
                              image: null,
                            }))
                          }
                          onRejected={(message) => {
                            setImageErrorBySectionId((prev) => ({
                              ...prev,
                              [section.id]: message,
                            }));
                            formik.setFieldTouched(
                              `sections.${sections.findIndex((entry) => entry.id === section.id)}.imageUrl`,
                              true,
                              true,
                            );
                          }}
                          uploadLabel="Upload section image"
                          uploadHint="Only 1 image is allowed for this section."
                        /> : null}

                        <div>
                          <p className="tour-add-form-label">description</p>
                          <button
                            type="button"
                            className={`field-label app-form-field field-preview-row ${readLocalizedValue(section?.descriptions?.[visibleLanguageIso]) ? "filled" : ""}`}
                            onClick={() =>
                              openDescriptionEditor(
                                section.id,
                                visibleLanguageIso,
                              )
                            }
                          >
                            {readLocalizedValue(
                              section?.descriptions?.[visibleLanguageIso],
                            ) ? (
                              <span
                                className="field-preview-value field-preview-value-html"
                                dangerouslySetInnerHTML={{
                                  __html: readLocalizedValue(
                                    section?.descriptions?.[visibleLanguageIso],
                                  ),
                                }}
                              />
                            ) : (
                              <span className="field-preview-value">
                                Complete
                              </span>
                            )}
                          </button>
                          {getFieldError(
                            `sections.${sections.findIndex((entry) => entry.id === section.id)}.descriptions.${visibleLanguageIso}`,
                          ) ? (
                            <InlineError>
                              {getFieldError(
                                `sections.${sections.findIndex((entry) => entry.id === section.id)}.descriptions.${visibleLanguageIso}`,
                              )}
                            </InlineError>
                          ) : null}
                        </div>

                        <div className="before-you-go-tool-items-head">
                          <strong>Items</strong>
                          <div className="before-you-go-tool-inline-actions">
                            <button
                              type="button"
                              className="app-icon-btn app-icon-btn--add before-you-go-tool-add-item-btn"
                              aria-label="Add item"
                              title="Add item"
                              onClick={() => handleAddItem(section.id)}
                            >
                              <IconPlus size={14} />
                            </button>
                          </div>
                        </div>

                        {Array.isArray(section.items) &&
                        section.items.length ? (
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={(event) =>
                              handleItemDragEnd(section.id, event)
                            }
                          >
                            <SortableContext
                              items={section.items.map((item) => item.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              <div className="before-you-go-tool-items-list">
                                {section.items.map((item) => (
                                  <SortableSectionItemCard
                                    key={item.id}
                                    item={item}
                                    visibleLanguageIso={visibleLanguageIso}
                                    onDelete={() =>
                                      handleRemoveItem(section.id, item.id)
                                    }
                                  >
                                    {isBeforeYouGoPanel && isEnglishLanguage ? (
                                      <div className="before-you-go-tool-item-grid">
                                        <label className="field-label app-form-field">
                                          <span className="label-inline">
                                            Type
                                            {getFieldError(
                                              `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.itemType`,
                                            ) ? (
                                              <InlineError>
                                                {getFieldError(
                                                  `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.itemType`,
                                                )}
                                              </InlineError>
                                            ) : null}
                                          </span>
                                          <select
                                            className="field-input select-control"
                                            value={item.itemType}
                                            onChange={(event) =>
                                              updateItemField(
                                                section.id,
                                                item.id,
                                                "itemType",
                                                event.target.value,
                                              )
                                            }
                                            onBlur={() =>
                                              formik.setFieldTouched(
                                                `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.itemType`,
                                                true,
                                                true,
                                              )
                                            }
                                          >
                                            <option value="hotel">Hotel</option>
                                            <option value="rentalCar">
                                              Rental Car
                                            </option>
                                          </select>
                                        </label>
                                      </div>
                                    ) : null}

                                    <div>
                                      <p className="tour-add-form-label">
                                        title
                                        {getFieldError(
                                          `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.title.${visibleLanguageIso}`,
                                        ) ? (
                                          <InlineError>
                                            {getFieldError(
                                              `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.title.${visibleLanguageIso}`,
                                            )}
                                          </InlineError>
                                        ) : null}
                                      </p>
                                      <label className="field-label app-form-field">
                                        <input
                                          className="field-input"
                                          value={
                                            item?.title?.[visibleLanguageIso] ||
                                            ""
                                          }
                                          onChange={(event) =>
                                            updateItemLocalizedField(
                                              section.id,
                                              item.id,
                                              "title",
                                              visibleLanguageIso,
                                              event.target.value,
                                            )
                                          }
                                          onBlur={() =>
                                            formik.setFieldTouched(
                                              `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.title.${visibleLanguageIso}`,
                                              true,
                                              true,
                                            )
                                          }
                                        />
                                      </label>
                                    </div>

                                    <label className="field-label app-form-field">
                                      <span className="label-inline">
                                        Location
                                        {getFieldError(
                                          `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.location`,
                                        ) ? (
                                          <InlineError>
                                            {getFieldError(
                                              `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.location`,
                                            )}
                                          </InlineError>
                                        ) : null}
                                      </span>
                                      <input
                                        className="field-input"
                                        value={item.location}
                                        onChange={(event) =>
                                          updateItemField(
                                            section.id,
                                            item.id,
                                            "location",
                                            event.target.value,
                                          )
                                        }
                                        onBlur={() =>
                                          formik.setFieldTouched(
                                            `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.location`,
                                            true,
                                            true,
                                          )
                                        }
                                      />
                                    </label>

                                    <div>
                                      <p className="tour-add-form-label">
                                        description
                                      </p>
                                      <button
                                        type="button"
                                        className={`field-label app-form-field field-preview-row ${readLocalizedValue(item?.description?.[visibleLanguageIso]) ? "filled" : ""}`}
                                        onClick={() =>
                                          openItemDescriptionEditor(
                                            section.id,
                                            item.id,
                                            visibleLanguageIso,
                                          )
                                        }
                                      >
                                        {readLocalizedValue(
                                          item?.description?.[
                                            visibleLanguageIso
                                          ],
                                        ) ? (
                                          <span
                                            className="field-preview-value field-preview-value-html"
                                            dangerouslySetInnerHTML={{
                                              __html: readLocalizedValue(
                                                item?.description?.[
                                                  visibleLanguageIso
                                                ],
                                              ),
                                            }}
                                          />
                                        ) : (
                                          <span className="field-preview-value">
                                            Complete
                                          </span>
                                        )}
                                      </button>
                                    </div>

                                    <label className="field-label app-form-field">
                                      <span className="label-inline">
                                        Website URL
                                        {getFieldError(
                                          `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.websiteUrl`,
                                        ) ? (
                                          <InlineError>
                                            {getFieldError(
                                              `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.websiteUrl`,
                                            )}
                                          </InlineError>
                                        ) : null}
                                      </span>
                                      <input
                                        className="field-input"
                                        value={item.websiteUrl}
                                        onChange={(event) =>
                                          updateItemField(
                                            section.id,
                                            item.id,
                                            "websiteUrl",
                                            event.target.value,
                                          )
                                        }
                                        onBlur={() =>
                                          formik.setFieldTouched(
                                            `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.websiteUrl`,
                                            true,
                                            true,
                                          )
                                        }
                                      />
                                    </label>

                                    {isEnglishLanguage ? <MediaPicker
                                      title="image"
                                      titleError={
                                        getFieldError(
                                          `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.imageUrl`,
                                        ) || ""
                                      }
                                      items={item?.image ? [item.image] : []}
                                      isUploading={Boolean(
                                        uploadingImageByItemId[item.id],
                                      )}
                                      uploadError={
                                        imageErrorByItemId[item.id] || ""
                                      }
                                      accept={{ "image/*": [] }}
                                      maxFiles={1}
                                      invalidTypeMessage="Image only for this item."
                                      onRejected={(message) => {
                                        setImageErrorByItemId((prev) => ({
                                          ...prev,
                                          [item.id]: message,
                                        }));
                                        formik.setFieldTouched(
                                          `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.imageUrl`,
                                          true,
                                          true,
                                        );
                                      }}
                                      onUpload={(files) =>
                                        handleUploadItemImage(
                                          section.id,
                                          item.id,
                                          files,
                                        )
                                      }
                                      onRemove={() =>
                                        updateItem(
                                          section.id,
                                          item.id,
                                          (currentItem) => ({
                                            ...currentItem,
                                            imageUrl: "",
                                            image: null,
                                          }),
                                        )
                                      }
                                      uploadLabel="Upload item image"
                                      uploadHint="Only 1 image is allowed for this item."
                                    /> : null}
                                  </SortableSectionItemCard>
                                ))}
                              </div>
                            </SortableContext>
                          </DndContext>
                        ) : (
                          <p className="before-you-go-tool-empty">
                            This section has no items yet.
                          </p>
                        )}
                      </div>
                    ) : null}
                  </SortableSectionCard>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>

      </section>

      <div className="mode-route-tool-steps">
        <button
          type="button"
          className={`app-icon-btn app-icon-btn--add mode-route-tool-route-btn ${isSaving || isImportingJson || typeof onSave !== "function" ? "is-disabled" : ""} ${isSaving || isImportingJson ? "is-loading" : ""}`}
          aria-label={
            isSaving
              ? `Saving ${String(panelTitle || "").toLowerCase()}`
              : `Save ${String(panelTitle || "").toLowerCase()}`
          }
          title={
            isSaving
              ? `Saving ${String(panelTitle || "").toLowerCase()}`
              : `Save ${String(panelTitle || "").toLowerCase()}`
          }
          onClick={handleSaveBeforeYouGo}
          disabled={
            isSaving ||
            isImportingJson ||
            typeof onSave !== "function" ||
            !formik.isValid
          }
        >
          {isSaving ? (
            <span className="mode-route-tool-btn-spinner" />
          ) : (
            <>
              Save
              <IconDeviceFloppy size={14} />
            </>
          )}
        </button>
      </div>

      <AppModal
        isOpen={editor.isOpen}
        onClose={closeDescriptionEditor}
        title={
          editor.target === "item"
            ? `description (${editor.languageIso})`
            : `description (${editor.languageIso})`
        }
        size="lg"
      >
        <div className="tour-add-editor">
          {!isDescriptionEditorReady ? (
            <p className="tour-add-editor-loading">Loading editor...</p>
          ) : null}
          <div ref={setQuillHost} className="tour-add-quill" />
          {shouldLimitActiveEditor ? (
            <small className="field-counter">
              {Math.min(
                editorPlainTextLength,
                BEFORE_YOU_GO_ITEM_DESCRIPTION_MAX_CHARS,
              )}
              /{BEFORE_YOU_GO_ITEM_DESCRIPTION_MAX_CHARS} characters
            </small>
          ) : null}
          <div className="tour-add-editor-actions">
            <button
              type="button"
              className="button-primary"
              onClick={saveDescriptionEditor}
            >
              Save
            </button>
            <button
              type="button"
              className="button-ghost"
              onClick={closeDescriptionEditor}
            >
              Cancel
            </button>
          </div>
        </div>
      </AppModal>
      <AppModal
        isOpen={isTranslateModalOpen}
        onClose={() => setIsTranslateModalOpen(false)}
        closeDisabled={isTranslating}
        title={
          translateTargetSectionTitle
            ? `AI Translate · ${translateTargetSectionTitle}`
            : `AI Translate ${panelTitle}`
        }
        size="sm"
        className="mode-route-translate-modal"
      >
        <p>
          {translateTargetSectionTitle
            ? "Translate this section's title and description, plus its item titles and descriptions, from English. Item locations are not included."
            : "Translate section titles and descriptions, plus item titles and descriptions, from English. Item locations are not included."}
        </p>
        <fieldset
          className="mode-route-translate-languages"
          disabled={isTranslating || translateComplete}
        >
          <legend>Target language</legend>
          {translationLanguages.map((language) => (
            <label key={language.iso}>
              <input
                type="radio"
                name="before-you-go-translation-language"
                value={language.iso}
                checked={translateLanguage === language.iso}
                onChange={() => setTranslateLanguage(language.iso)}
              />
              <span>{language.name || language.iso.toUpperCase()}</span>
              <small>{language.iso.toUpperCase()}</small>
            </label>
          ))}
        </fieldset>
        {!hasTranslateTarget ? <p>No sections are available to translate.</p> : null}
        {!translationLanguages.length ? (
          <p>No translation languages are available for this Tour.</p>
        ) : null}
        {isTranslating ? <ThinkingAnimation label="Translating sections..." /> : null}
        {translateProgress.points.length ? (
          <section
            className={`mode-route-ai-stream ${isTranslating ? "is-running" : "is-finished"}`}
            aria-live="polite"
          >
            <div className="mode-route-ai-stream-heading">
              <strong>
                {translateProgress.done
                  ? "Translation summary"
                  : `Translating ${translateTargetSectionTitle || panelTitle}`}
              </strong>
                <span>
                  {translationCompleted} / {translationTotal}{" "}
                  {translateSectionId === null ? "sections" : "section"}
                </span>
            </div>
            <div
              className="mode-route-ai-stream-track"
              role="progressbar"
              aria-label={`${translateTargetSectionTitle || panelTitle} translation progress`}
              aria-valuemin={0}
              aria-valuemax={translationTotal}
              aria-valuenow={Math.min(translationCompleted, translationTotal)}
            >
              <span style={{ width: `${translationPercent}%` }} />
            </div>
            <div className="mode-route-ai-stream-counts">
              <span className="is-completed">{translationCounts.completed || 0} translated</span>
              <span className="is-error">{translationCounts.error || 0} errors</span>
              <span>{translationCounts.pending || 0} waiting</span>
            </div>
            <ol className="mode-route-ai-stream-list">
              {translateProgress.points.map((point, index) => {
                const status = String(point?.status || "pending");
                const statusLabels = {
                  completed: "Translated",
                  error: "Error",
                  pending: "Waiting",
                };
                const statusIcons = { completed: "✓", error: "!", pending: "…" };
                return (
                  <li className={`is-${status}`} key={point.id}>
                    <span className="mode-route-ai-stream-status" aria-hidden="true">
                      {statusIcons[status] || "•"}
                    </span>
                    <span className="mode-route-ai-stream-point">
                      <strong>#{index + 1} · {point.label}</strong>
                      {point.translatedName ? <small>{point.translatedName}</small> : null}
                      {point.message ? <small>{point.message}</small> : null}
                    </span>
                    <span className="mode-route-ai-stream-label">
                      {statusLabels[status] || status}
                    </span>
                  </li>
                );
              })}
            </ol>
          </section>
        ) : null}
        {translateComplete ? (
          <p className="mode-route-translate-success" role="status">
            Translation complete. Review the selected language before saving.
          </p>
        ) : null}
        {translateError ? (
          <p className="message-error" role="alert">{translateError}</p>
        ) : null}
        <div className="tour-add-editor-actions">
          <button
            type="button"
            className="button-ghost"
            onClick={() => setIsTranslateModalOpen(false)}
            disabled={isTranslating}
          >
            {translateProgress.done ? "Close" : "Cancel"}
          </button>
          {!translateComplete ? (
            <button
              type="button"
              className="button-primary"
              onClick={handleTranslateSections}
              disabled={
                !hasTranslateTarget ||
                !translateLanguage ||
                isTranslating ||
                !translationLanguages.length
              }
            >
              {isTranslating ? "Translating..." : "Translate"}
            </button>
          ) : null}
        </div>
      </AppModal>
      <AiToolsPanel
        anchorRef={aiToolsAnchorRef}
        title="AI TOOLS"
        ariaLabel={`${panelTitle} AI Tools`}
        isVisible={
          supportsJsonImport &&
          hasResolvedActiveLanguage &&
          isEnglishLanguage
        }
        actions={[
          {
            id: "translate-tour-section",
            label: "AI Translate",
            icon: <IconLanguage size={17} />,
            onClick: openTranslateModal,
          },
        ]}
      />
    </aside>
  );
}

export default BaseTourSectionPanel;
